import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { ClientContractFilters, type ClientContractFilterValues } from './client-contract-filters';
import type { ClientContractEntry } from './client-contract-table';
import { ClientContractTable } from './client-contract-table';
import { ClientContractsEmptyState } from './client-contracts-empty-state';

interface ClientContractsTabProps {
  entries: ClientContractEntry[];
  onCreateContract?: () => void;
  onEdit?: (entry: ClientContractEntry) => void;
  onToggleActive?: (entry: ClientContractEntry) => void;
  onHistory?: (entry: ClientContractEntry) => void;
  onPublishVersion?: (entry: ClientContractEntry) => void;
  onApplyFilters?: (values: ClientContractFilterValues) => void;
  onClearFilters?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
}

export function ClientContractsTab({
  entries,
  onCreateContract,
  onEdit,
  onToggleActive,
  onHistory,
  onPublishVersion,
  onApplyFilters,
  onClearFilters,
  isLoading,
  isError,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
}: ClientContractsTabProps) {
  const hasEntries = entries.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Contratos por Cliente</h2>
          <p className="text-sm text-muted-foreground">
            Acompanhe os contratos individualizados por cliente.
          </p>
        </div>
        <Button
          type="button"
          className="bg-black text-white hover:bg-gray-800"
          onClick={onCreateContract}
          disabled={!onCreateContract}
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo contrato
        </Button>
      </div>

      <ClientContractFilters onApply={onApplyFilters} onClear={onClearFilters} />

      {isLoading ? (
        <div className="flex justify-center rounded-md border bg-white py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 py-12 text-center text-sm text-destructive">
          Não foi possível carregar os contratos.
        </div>
      ) : hasEntries ? (
        <ClientContractTable
          entries={entries}
          onEdit={onEdit}
          onToggleActive={onToggleActive}
          onHistory={onHistory}
          onPublishVersion={onPublishVersion}
        />
      ) : (
        <ClientContractsEmptyState />
      )}

      {!isLoading && !isError && totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          limit={limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
